import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import api from "@/api/client";
import { ChevronDown, ChevronLeft, ChevronRight, ArrowLeft, Search, X, ThumbsUp, ThumbsDown, Mail } from "lucide-react";
import { toast } from "sonner";
import EditorJsRenderer from "@/components/EditorJsRenderer";
import TopicHighlight from "@/components/TopicHighlight";
import { filterTopicArticles, parseSearchTerms } from "@/lib/topicSearch";

const TXT = {
  en: {
    home: "Home",
    backHome: "Back to Home",
    helpCenter: "Help Center",
    questions: "questions",
    prev: "Previous",
    next: "Next",
    helpfulQ: "Was this answer helpful?",
    yes: "Yes",
    no: "No",
    thanks: "Thanks for your feedback!",
    stillHaveQ: "Still have questions?",
    cta: "Our support team is here to help.",
    contact: "Contact",
    feedbackMsg: "What could we improve? (optional)",
    sendFeedback: "Send feedback",
    sections: "Sections",
    topics: "Topics",
    searchTopic: "Search questions…",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    loading: "Loading…",
    noMatch: "No questions match your search.",
    matchCount: "matches",
    searchingIn: "Searching in",
    noQuestions: "No questions yet.",
  },
  ar: {
    home: "الرئيسية",
    backHome: "العودة إلى الرئيسية",
    helpCenter: "مركز المساعدة",
    questions: "أسئلة",
    prev: "السابق",
    next: "التالي",
    helpfulQ: "هل كانت هذه الإجابة مفيدة؟",
    yes: "نعم",
    no: "لا",
    thanks: "شكراً على ملاحظاتك!",
    stillHaveQ: "هل لديك أسئلة أخرى؟",
    cta: "فريق الدعم لدينا متاح لمساعدتك.",
    contact: "تواصل",
    feedbackMsg: "ما الذي يمكن تحسينه؟ (اختياري)",
    sendFeedback: "إرسال",
    sections: "الأقسام",
    topics: "المواضيع",
    searchTopic: "ابحث في الأسئلة…",
    expandAll: "فتح الكل",
    collapseAll: "إغلاق الكل",
    loading: "جاري التحميل…",
    noMatch: "لا توجد أسئلة تطابق البحث.",
    matchCount: "نتائج",
    searchingIn: "البحث في",
    noQuestions: "لا توجد أسئلة بعد.",
  },
};

export default function PublicCategoryPage() {
  const { categorySlug } = useParams();
  const { space, slug, basePath = `/s/${slug}`, lang, supportEmail, primary } = useOutletContext();
  const t = TXT[lang || "en"];
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get("sub");

  const [topicData, setTopicData] = useState(null);
  const [loadedSlug, setLoadedSlug] = useState(null);
  const [loadingTopic, setLoadingTopic] = useState(true);
  const [topics, setTopics] = useState([]);
  const [activeSubId, setActiveSubId] = useState(null);
  const [filter, setFilter] = useState("");
  const [openIds, setOpenIds] = useState(new Set());
  const [feedback, setFeedback] = useState({});
  const mainRef = useRef(null);
  const topicCacheRef = useRef(new Map());

  const fetchTopic = (topicSlug) => {
    const cacheKey = `${slug}:${lang}:${topicSlug}`;
    const cached = topicCacheRef.current.get(cacheKey);
    if (cached) return cached;
    const promise = api
      .get(`/public/topics/${topicSlug}?space=${encodeURIComponent(slug)}&lang=${lang}`)
      .then((r) => {
        topicCacheRef.current.set(cacheKey, Promise.resolve(r.data));
        return r.data;
      });
    topicCacheRef.current.set(cacheKey, promise);
    return promise;
  };

  useEffect(() => {
    let cancelled = false;
    setLoadingTopic(true);
    setFilter("");
    setFeedback({});

    fetchTopic(categorySlug).then((data) => {
      if (cancelled) return;
      setTopicData(data);
      setLoadedSlug(categorySlug);
      const sections = data.sections || [];
      if (sections.length) {
        const found = subParam ? sections.find((s) => s.slug === subParam) : null;
        setActiveSubId((found || sections[0]).id);
      } else {
        setActiveSubId(null);
      }
    }).finally(() => {
      if (!cancelled) setLoadingTopic(false);
    });

    return () => { cancelled = true; };
  }, [slug, categorySlug, lang, subParam]);

  useEffect(() => {
    api.get(`/public/categories?space=${encodeURIComponent(slug)}&lang=${lang}`).then((r) => {
      setTopics((r.data || []).filter((c) => !c.parent_id));
    });
  }, [slug, lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [categorySlug]);

  useEffect(() => {
    if (!topicData || !activeSubId || loadedSlug !== categorySlug) return;
    const sec = topicData.sections.find((s) => s.id === activeSubId);
    if (sec && sec.slug !== subParam) setSearchParams({ sub: sec.slug }, { replace: true });
  }, [activeSubId, topicData, loadedSlug, categorySlug, subParam, setSearchParams]);

  const contentReady = Boolean(topicData && loadedSlug === categorySlug && !loadingTopic);
  const sections = contentReady ? (topicData.sections || []) : [];
  const activeIdx = sections.findIndex((s) => s.id === activeSubId);
  const active = sections[activeIdx];
  const prev = activeIdx > 0 ? sections[activeIdx - 1] : null;
  const next = activeIdx < sections.length - 1 ? sections[activeIdx + 1] : null;

  const searchTerms = useMemo(() => parseSearchTerms(filter), [filter]);
  const isSearching = searchTerms.length > 0;

  const allTopicArticles = useMemo(() => {
    if (!sections.length) return [];
    return sections.flatMap((section) =>
      (section.articles || []).map((article) => ({
        ...article,
        sectionName: section.name,
        sectionIcon: section.icon,
      }))
    );
  }, [sections]);

  const filteredArticles = useMemo(() => {
    const pool = isSearching ? allTopicArticles : (active?.articles || []);
    const hits = filterTopicArticles(pool, filter);
    return hits.map(({ article, terms, score }) => ({
      ...article,
      _searchTerms: terms,
      _searchScore: score,
    }));
  }, [active, allTopicArticles, filter, isSearching]);

  useEffect(() => {
    setOpenIds(new Set(filteredArticles.map((a) => a.id)));
  }, [filteredArticles]);

  const allAccordionsOpen = filteredArticles.length > 0 && filteredArticles.every((a) => openIds.has(a.id));

  const toggleAllAccordions = () => {
    if (allAccordionsOpen) {
      setOpenIds(new Set());
    } else {
      setOpenIds(new Set(filteredArticles.map((a) => a.id)));
    }
  };

  const toggleAccordion = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        next.add(id);
        api.post(`/public/analytics/event?space=${encodeURIComponent(slug)}`, {
          event_type: "article_view",
          article_id: id,
          visitor_id: localStorage.getItem("visitor_id") || "",
        }).catch(() => {});
      }
      return next;
    });
  };

  const submitFeedback = async (articleId, type, message) => {
    setFeedback((p) => ({ ...p, [articleId]: "sending" }));
    try {
      await api.post(`/public/feedback?space=${encodeURIComponent(slug)}`, {
        article_id: articleId, type, message: message || "",
        visitor_id: localStorage.getItem("visitor_id") || "",
      });
      setFeedback((p) => ({ ...p, [articleId]: "sent" }));
      toast.success(t.thanks);
    } catch {
      setFeedback((p) => ({ ...p, [articleId]: null }));
    }
  };

  const goToSection = (id) => {
    setActiveSubId(id);
    setFilter("");
    setFeedback({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="bg-[var(--pp-bg)] min-h-[calc(100vh-128px)] relative">
      {/* Faint background glow */}
      <div className="absolute pointer-events-none top-0 left-0 right-0 h-96 opacity-40"
        style={{ background: `radial-gradient(ellipse 60% 100% at 50% 0%, ${primary}15 0%, transparent 70%)` }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6" ref={mainRef}>
          {/* SIDEBAR — all topics */}
          <aside className="lg:sticky lg:top-24 self-start">
            <Link to={basePath || "/"}
              className="flex items-center gap-2 mb-5 text-xs text-[var(--pp-text-muted)] hover:text-[var(--pp-text)] transition-colors"
              data-testid="back-to-home">
              <ArrowLeft className="w-3.5 h-3.5 rtl:rotate-180" />
              <span>{t.backHome}</span>
            </Link>

            <div className="text-[10px] uppercase tracking-[0.3em] font-black mb-3 px-1 text-[var(--pp-text-dim)]">
              {t.topics}
            </div>

            <nav className="space-y-1" data-testid="topic-sidebar">
              {topics.map((topic) => {
                const isActive = topic.slug === categorySlug;
                return (
                  <Link
                    key={topic.id}
                    to={`${basePath}/c/${topic.slug}`}
                    onMouseEnter={() => fetchTopic(topic.slug)}
                    data-testid={`topic-nav-${topic.slug}`}
                    className={`block w-full ltr:text-left rtl:text-right rounded-xl transition-all relative group ${isActive ? "active-sidebar-glow overflow-hidden" : "overflow-hidden"}`}
                    style={isActive ? {
                      background: `linear-gradient(90deg, ${primary}22, transparent 70%)`,
                      "--pp-accent": primary,
                      "--pp-accent-30": `${primary}66`,
                      "--pp-accent-55": `${primary}AA`,
                      "--pp-accent-88": `${primary}DD`,
                    } : { border: "1px solid transparent" }}
                  >
                    <div className="flex items-center justify-between gap-3 px-4 py-3">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-base">{topic.icon || "📂"}</span>
                        <span className={`text-sm font-semibold truncate ${isActive ? "text-[var(--pp-text)]" : "text-[var(--pp-text-muted)] group-hover:text-[var(--pp-text)]"}`}>
                          {topic.name}
                        </span>
                      </div>
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded-full shrink-0 ${
                        isActive ? "bg-[var(--pp-text)]/10 text-[var(--pp-text)]" : "bg-[var(--pp-surface)] text-[var(--pp-text-dim)]"
                      }`}>
                        {topic.article_count ?? 0}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </nav>
          </aside>

          {/* MAIN */}
          <main className="min-w-0">
            {/* Topic-local search — filters listed articles in current language only */}
            <div className="relative mb-6">
              <Search className="w-4 h-4 absolute ltr:left-4 rtl:right-4 top-1/2 -translate-y-1/2 text-[var(--pp-text-dim)]" />
              <input
                value={filter} onChange={(e) => setFilter(e.target.value)}
                placeholder={t.searchTopic}
                disabled={!contentReady}
                data-testid="subtopic-filter"
                className="w-full ltr:pl-11 ltr:pr-11 rtl:pr-11 rtl:pl-11 py-3 bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] focus:border-[var(--pp-accent)] rounded-full text-[var(--pp-text)] placeholder-[var(--pp-text-dim)] focus:outline-none transition-colors disabled:opacity-60"
              />
              {filter && (
                <button onClick={() => setFilter("")} className="absolute ltr:right-4 rtl:left-4 top-1/2 -translate-y-1/2 text-[var(--pp-text-dim)]">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {!contentReady && (
              <div className="flex items-center justify-center min-h-[280px] text-sm text-[var(--pp-text-muted)]" data-testid="topic-content-loading">
                <span className="inline-flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full border-2 border-[var(--pp-border)] border-t-[var(--pp-accent)] animate-spin" />
                  {t.loading}
                </span>
              </div>
            )}

            {isSearching && contentReady && (
              <div className="mb-4 text-xs text-[var(--pp-text-muted)]" data-testid="topic-search-count">
                {filteredArticles.length} {t.matchCount} {t.searchingIn} {topicData?.topic?.name || ""}
              </div>
            )}

            {contentReady && sections.length > 1 && !isSearching && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="subtopic-chips">
                {sections.map((s) => {
                  const isActive = s.id === activeSubId;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => goToSection(s.id)}
                      data-testid={`subtopic-${s.slug}`}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all border ${
                        isActive
                          ? "text-[var(--pp-text)] border-transparent"
                          : "text-[var(--pp-text-muted)] border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] hover:text-[var(--pp-text)]"
                      }`}
                      style={isActive ? { background: `linear-gradient(135deg, ${primary}, ${primary}dd)`, boxShadow: `0 0 16px ${primary}44` } : {}}
                    >
                      {s.icon || "📂"} {s.name}
                    </button>
                  );
                })}
              </div>
            )}

            {contentReady && (isSearching || active) && (
              <div key={categorySlug} className="fade-in">
                {/* Active sub-topic header */}
                <header className="mb-8">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="text-4xl leading-none">{isSearching ? (topicData?.topic?.icon || "📂") : (active?.icon || "📂")}</span>
                    <h1 className="font-display text-4xl sm:text-5xl text-[var(--pp-text)] leading-none" data-testid="subtopic-title">
                      {isSearching ? (topicData?.topic?.name || active?.name) : active?.name}
                    </h1>
                  </div>
                  {!isSearching && active?.description && (
                    <p className="text-[var(--pp-text-muted)] text-base leading-relaxed max-w-3xl">{active.description}</p>
                  )}
                  <div className="mt-5 h-px w-full" style={{ background: `linear-gradient(90deg, ${primary}55, transparent)` }} />
                </header>

                {/* Accordion */}
                <div className="mb-10" data-testid="questions-accordion">
                  {filteredArticles.length > 0 && (
                    <div className="flex justify-end mb-3">
                      <button
                        type="button"
                        onClick={toggleAllAccordions}
                        data-testid="toggle-all-accordion"
                        className="text-[11px] font-black uppercase tracking-[0.2em] text-[var(--pp-text-muted)] hover:text-[var(--pp-text)] transition-colors px-3 py-1.5 rounded-lg border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)]"
                      >
                        {allAccordionsOpen ? t.collapseAll : t.expandAll}
                      </button>
                    </div>
                  )}
                  <div className="space-y-3">
                  {filteredArticles.length === 0 ? (
                    <div className="bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-2xl p-10 text-center text-sm text-[var(--pp-text-muted)]">
                      {filter ? t.noMatch : t.noQuestions}
                    </div>
                  ) : filteredArticles.map((a) => {
                    const isOpen = openIds.has(a.id);
                    const fb = feedback[a.id];
                    const terms = a._searchTerms || [];
                    return (
                      <div key={a.id}
                        data-testid={`q-${a.slug}`}
                        className="bg-[var(--pp-surface)] border rounded-2xl overflow-hidden transition-all"
                        style={{ borderColor: isOpen ? `${primary}55` : "var(--pp-border)" }}>
                        <button onClick={() => toggleAccordion(a.id)}
                          className="w-full flex items-center justify-between gap-3 px-6 py-5 ltr:text-left rtl:text-right hover:bg-[var(--pp-surface-hover)] transition-colors">
                          <h3 className="font-semibold text-base text-[var(--pp-text)] leading-snug ltr:pr-4 rtl:pl-4 flex-1 ltr:text-left rtl:text-right"
                            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.005em" }}>
                            <TopicHighlight text={a.title} terms={terms} />
                          </h3>
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[var(--pp-surface-hover)] border border-[var(--pp-border)] flex items-center justify-center transition-transform"
                            style={{ transform: isOpen ? "rotate(180deg)" : "none", borderColor: isOpen ? `${primary}55` : "var(--pp-border)" }}>
                            <ChevronDown className="w-4 h-4" style={{ color: isOpen ? primary : "var(--pp-text-muted)" }} />
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-6 pt-1 fade-in">
                            <div className="h-px mb-5 w-full" style={{ background: `linear-gradient(90deg, ${primary}33, transparent)` }} />
                            {isSearching && a.sectionName && (
                              <div className="text-[10px] uppercase tracking-wider font-bold text-[var(--pp-text-dim)] mb-3">
                                {a.sectionIcon || "📂"} {a.sectionName}
                              </div>
                            )}
                            <EditorJsRenderer
                              content={a.content}
                              className="prose-doc text-[15px]"
                              testid={`q-content-${a.slug}`}
                              highlightTerms={terms}
                            />

                            {space.settings?.show_ratings !== false && (
                              <div className="mt-6 pt-5 border-t border-[var(--pp-border)]">
                                {fb === "sent" ? (
                                  <div className="text-xs font-bold" style={{ color: primary }} data-testid={`fb-thanks-${a.slug}`}>
                                    ✓ {t.thanks}
                                  </div>
                                ) : fb === "not_helpful" ? (
                                  <NotHelpfulForm t={t} onSubmit={(msg) => submitFeedback(a.id, "not_helpful", msg)} primary={primary} testId={`fb-form-${a.slug}`} />
                                ) : (
                                  <div className="flex items-center gap-3 flex-wrap">
                                    <span className="text-xs font-bold text-[var(--pp-text-muted)]">{t.helpfulQ}</span>
                                    <button onClick={() => submitFeedback(a.id, "helpful")}
                                      data-testid={`fb-yes-${a.slug}`}
                                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold border border-[var(--pp-border)] hover:border-emerald-500 hover:text-emerald-400 text-[var(--pp-text-muted)] rounded-full transition-colors">
                                      <ThumbsUp className="w-3 h-3" /> {t.yes}
                                    </button>
                                    <button onClick={() => setFeedback((p) => ({ ...p, [a.id]: "not_helpful" }))}
                                      data-testid={`fb-no-${a.slug}`}
                                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold border border-[var(--pp-border)] hover:border-rose-500 hover:text-rose-400 text-[var(--pp-text-muted)] rounded-full transition-colors">
                                      <ThumbsDown className="w-3 h-3" /> {t.no}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>

                {/* PREV / NEXT */}
                <div className="grid grid-cols-2 gap-3 mb-10" data-testid="prev-next-nav">
                  {prev ? (
                    <button onClick={() => goToSection(prev.id)} data-testid="prev-section-btn"
                      className="text-left bg-[var(--pp-surface)] border border-[var(--pp-border)] hover:border-[var(--pp-border-hover)] rounded-xl p-5 group transition-all">
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-black text-[var(--pp-text-dim)] mb-1.5">
                        <ChevronLeft className="w-3 h-3 rtl:rotate-180" /> {t.prev}
                      </div>
                      <div className="font-semibold text-base text-[var(--pp-text)]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {prev.icon} {prev.name}
                      </div>
                    </button>
                  ) : <div />}
                  {next ? (
                    <button onClick={() => goToSection(next.id)} data-testid="next-section-btn"
                      className="text-right bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-xl p-5 group transition-all col-start-2"
                      style={{ boxShadow: `0 0 0 1px ${primary}22` }}>
                      <div className="flex items-center justify-end gap-2 text-[10px] uppercase tracking-[0.25em] font-black mb-1.5" style={{ color: primary }}>
                        {t.next} <ChevronRight className="w-3 h-3 rtl:rotate-180" />
                      </div>
                      <div className="font-semibold text-base text-[var(--pp-text)]" style={{ fontFamily: "Outfit, sans-serif" }}>
                        {next.name} {next.icon}
                      </div>
                    </button>
                  ) : null}
                </div>

                {/* Still have questions */}
                <div className="bg-[var(--pp-surface)] border border-[var(--pp-border)] rounded-2xl p-8 text-center relative overflow-hidden">
                  <div className="absolute pointer-events-none top-0 left-0 right-0 h-full opacity-30"
                    style={{ background: `radial-gradient(ellipse 60% 60% at 50% 0%, ${primary}30 0%, transparent 60%)` }} />
                  <div className="relative">
                    <h4 className="font-display text-3xl mb-2 text-[var(--pp-text)]">{t.stillHaveQ}</h4>
                    <p className="text-sm text-[var(--pp-text-muted)] mb-5">{t.cta}</p>
                    <a href={`mailto:${supportEmail}`}
                      data-testid="contact-support-bottom"
                      className="inline-flex items-center gap-2 px-5 py-3 text-[var(--pp-text)] font-black text-xs uppercase tracking-[0.25em] rounded-full hover:opacity-90 hover:scale-[1.02] transition-all"
                      style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)`, boxShadow: `0 0 24px ${primary}55` }}>
                      <Mail className="w-3.5 h-3.5" /> {t.contact} — {supportEmail}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function NotHelpfulForm({ t, onSubmit, primary, testId }) {
  const [msg, setMsg] = useState("");
  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="text-xs font-bold text-[var(--pp-text-muted)] block">{t.feedbackMsg}</label>
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={2}
        className="w-full px-3 py-2 bg-[var(--pp-surface-hover)] border border-[var(--pp-border)] focus:border-[var(--pp-accent)] rounded-xl text-sm text-[var(--pp-text)] placeholder-[var(--pp-text-dim)] focus:outline-none" />
      <button onClick={() => onSubmit(msg)}
        className="text-xs font-black uppercase tracking-wider px-4 py-2 text-[var(--pp-text)] rounded-full"
        style={{ background: `linear-gradient(135deg, ${primary}, ${primary}dd)` }}>
        {t.sendFeedback}
      </button>
    </div>
  );
}
